import {
  DistanceStatic,
  useTranslation,
} from '../../../contexts/TranslationContext';
import {
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  ReferenceLine,
  TooltipProps,
  Legend,
} from 'recharts';
import {
  ValueType,
  NameType,
} from 'recharts/types/component/DefaultTooltipContent';
import { deviceDataInterface } from '../../../models/interfaces/device_interfaces';
import { Paper, useTheme } from '@mui/material';
import { MHMTypography } from '../MHM/MHMTypography';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { RefObject, useMemo } from 'react';
import { LegendContent } from './DeviceGraphLegend';

const formatDate = (
  date: number,
  language: string,
  date_format: Intl.DateTimeFormatOptions,
) => {
  const numericDate = new Date(date * 1000);
  return numericDate.toLocaleDateString(language, date_format);
};

const formatTick = (value: number, distance: DistanceStatic) => {
  return distance.show(distance.to_metric(value));
};

const renderTooltip = (
  show_water_level: boolean,
  show_rainfall_level: boolean,
  max_height: number,
) =>
  function Tooltip(props: TooltipProps<ValueType, NameType>) {
    const { t, language, distance } = useTranslation();

    if (
      !props.payload ||
      props.payload.length === 0 ||
      (props.payload[0].payload.fake_water_level &&
        props.payload[0].payload.fake_rainfall_level) ||
      (props.payload[0].payload.fake_water_level && !show_rainfall_level) ||
      (props.payload[0].payload.fake_rainfall_level && !show_water_level)
    ) {
      return <></>;
    }

    const waterLevel = distance.show(
      distance.to_metric(props.payload[0].payload.water_level_mm),
    );
    const fillPercentage = (
      (distance.to_metric(props.payload[0].payload.water_level_mm) /
        max_height) *
      100
    ).toFixed(1);
    const rainfallLevel = distance.show(
      distance.to_metric(props.payload[0].payload.rainfall_level_mm),
    );

    return (
      <Paper
        sx={{
          border: 'ridge',
          borderColor: '',
          borderWidth: '1',
          p: '4px',
        }}
      >
        <MHMTypography>
          {formatDate(props.label, language, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
          })}
        </MHMTypography>
        {show_water_level &&
          props.payload[0].payload.water_level_mm !== undefined && (
            <>
              <MHMTypography color="primary">
                {t('Water Level:')} {waterLevel}
              </MHMTypography>
              <MHMTypography color="primary">
                {t('Fill:')} {fillPercentage}%
              </MHMTypography>
            </>
          )}
        {show_rainfall_level &&
          props.payload[0].payload.rainfall_level_mm !== undefined && (
            <MHMTypography color="railfall">
              {t('Rainfall Level:')} {rainfallLevel}
            </MHMTypography>
          )}
      </Paper>
    );
  };

export type DeviceData = {
  water_data: { water_level: number; time: number }[];
  rain_data: { rainfall_level: number; time: number }[];
};

export type DeviceDataFunction = (data: DeviceData) => DeviceData;

export type DeviceGraphProps = {
  title?: string;
  data: deviceDataInterface;
  show_water_level: boolean;
  show_rainfall: boolean;
  show_bench_height: boolean;
  bench_height_mm: number | undefined;
  show_pipe_diameter: boolean;
  pipe_diameter_mm: number | undefined;
  max_height: number;
  warning_threshold: number;
  min_time: number;
  max_time: number;
  fit_y_axis_to_data?: boolean;
  containerRef?: RefObject<HTMLDivElement>;
  isFiltered: boolean;
  peakWaterTime: number | null;
  peakRainTime: number | null;
  show_maximums: boolean;
};

export const DeviceGraph = (props: DeviceGraphProps) => {
  const { t, language, distance } = useTranslation();
  const { palette } = useTheme();
  const primary = palette.primary.main;
  const rainfall = palette.rainfall.main;
  const pipe_diameter = palette.pipe_diameter.main;
  const bench_height = palette.bench_height.main;
  const isMobile = useIsMobile();

  const pipeDiameterText = useMemo(() => {
    if (!props.pipe_diameter_mm) {
      return 0;
    }

    // ft-in for en-US, mm for other languages
    const diameterDefault = distance.show(props.pipe_diameter_mm);
    const diameterCM = distance.show(props.pipe_diameter_mm, {
      unit: 'cm',
    });
    if (language !== 'en-US') {
      return `${diameterCM} (${diameterDefault})`;
    }
    const diameterInches = distance.show(props.pipe_diameter_mm, {
      unit: 'in',
    });
    return props.pipe_diameter_mm < 304 // 1 foot in mm, rounded down to commodate for floating point errors
      ? diameterInches
      : `${diameterDefault} (${diameterInches})`;
  }, [language, props.pipe_diameter_mm, distance]);

  if (!(props.show_water_level || props.show_rainfall)) {
    return <></>;
  }

  let water_data = props.data.water_data;

  water_data = water_data.map(x => {
    return {
      measurement_unix_timestamp: x.measurement_unix_timestamp,
      water_level_mm: distance.from_metric(x.water_level_mm),
    };
  });

  let rain_data = props.data.rainfall_data;

  rain_data = rain_data.map(x => {
    return {
      measurement_unix_timestamp: x.measurement_unix_timestamp,
      rainfall_level_mm: distance.from_metric(x.rainfall_level_mm),
    };
  });

  const min_time = props.min_time;
  const max_time = props.max_time;

  // Compute Y-axis domain for water level based on toggle
  const backendMax = distance.from_metric(props.max_height);
  const dataWaterMax = water_data.length
    ? Math.max(...water_data.map(d => d.water_level_mm))
    : 0;
  const buffer = Math.max(dataWaterMax * 0.05, 1); // small buffer for better UX/hover
  const waterYAxisMax = props.fit_y_axis_to_data
    ? Math.max(dataWaterMax + buffer, 1)
    : backendMax;

  let ticks: number[] | undefined = undefined;
  let interval: number | undefined = undefined;
  if (language === 'en-US') {
    let step = 12;
    if (waterYAxisMax <= 6) {
      step = 2;
    } else if (waterYAxisMax <= 12) {
      step = 3;
    } else if (waterYAxisMax <= 24) {
      step = 6;
    }

    ticks = Array(Math.ceil(waterYAxisMax / step))
      .fill(0)
      .map((_, i) => i * step);
    interval = Math.floor(ticks.length / 5);
  }

  return (
    <div ref={props.containerRef}>
      <ResponsiveContainer
        width={'100%'}
        height={300}
        style={{
          paddingRight:
            isMobile || (props.show_rainfall && props.show_water_level)
              ? 0
              : 70,
        }}
      >
        <LineChart
          height={300}
          style={{
            fontSize: '1rem',
            fontWeight: 300,
            margin: 0,
            padding: 0,
          }}
        >
          <CartesianGrid stroke="#ccc" />
          <Tooltip
            isAnimationActive={false}
            content={renderTooltip(
              props.show_water_level,
              props.show_rainfall,
              props.max_height,
            )}
          />
          <Legend
            verticalAlign="bottom"
            height={50}
            content={<LegendContent />}
            payload={
              props.show_maximums && (props.peakWaterTime || props.peakRainTime)
                ? [
                    ...(props.show_water_level
                      ? [
                          {
                            value: props.isFiltered
                              ? t('Water Level Filtered (mm)')
                              : t('Water Level Raw (mm)'),
                            type: 'line' as const,
                            id: 'water',
                            color: primary,
                          },
                        ]
                      : []),
                    ...(props.show_water_level && props.peakWaterTime
                      ? [
                          {
                            value: t('Max water level in selected range'),
                            type: 'rect' as const,
                            id: 'peak-water',
                            color: `${primary}33`,
                          },
                        ]
                      : []),
                    ...(props.show_rainfall
                      ? [
                          {
                            value: t('Rainfall Level (mm)'),
                            type: 'line' as const,
                            id: 'rainfall',
                            color: rainfall,
                          },
                        ]
                      : []),
                    ...(props.show_rainfall && props.peakRainTime
                      ? [
                          {
                            value: t('Max rainfall in selected range'),
                            type: 'rect' as const,
                            id: 'peak-rain',
                            color: `${rainfall}33`,
                          },
                        ]
                      : []),
                  ]
                : undefined
            }
          />

          {props.show_water_level &&
            props.peakWaterTime &&
            props.show_maximums && (
              <ReferenceLine
                x={props.peakWaterTime}
                xAxisId="time"
                stroke={primary}
                strokeWidth={8}
                strokeOpacity={0.2}
                yAxisId="water"
              />
            )}

          {props.show_rainfall && props.peakRainTime && props.show_maximums && (
            <ReferenceLine
              x={props.peakRainTime}
              xAxisId="time"
              stroke={rainfall}
              strokeWidth={8}
              strokeOpacity={0.2}
              yAxisId="rainfall"
            />
          )}

          <XAxis
            xAxisId="time"
            dataKey="measurement_unix_timestamp"
            tickFormatter={date =>
              formatDate(date, language, {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })
            }
            ticks={Array.from(new Set([min_time, max_time]))}
            type="number"
            scale="time"
            allowDataOverflow
            domain={[min_time, max_time]}
          />
          {props.show_water_level && (
            <>
              <Line
                isAnimationActive={false}
                type="monotone"
                yAxisId="water"
                xAxisId="time"
                dataKey="water_level_mm"
                name={
                  props.isFiltered
                    ? t('Water Level Filtered (mm)')
                    : t('Water Level Raw (mm)')
                }
                stroke={primary}
                strokeWidth="3px"
                dot={false}
                data={water_data}
              />
              <YAxis
                yAxisId="water"
                dataKey="water_level_mm"
                width={isMobile ? undefined : 70}
                stroke={primary}
                domain={[0, waterYAxisMax]}
                scale="linear"
                tickFormatter={value => formatTick(value, distance)}
                ticks={ticks}
                interval={interval}
              ></YAxis>
              <ReferenceLine
                y={distance.from_metric(props.warning_threshold)}
                stroke="grey"
                strokeDasharray="3 3"
                xAxisId="time"
                yAxisId="water"
                label={{
                  value: `${t('Warning Threshold:')} ${((props.warning_threshold / props.max_height) * 100).toFixed(1)}% (${distance.show(props.warning_threshold)})`,
                  position: 'top',
                }}
              />
            </>
          )}
          {props.show_rainfall && (
            <>
              <Line
                isAnimationActive={false}
                type="monotone"
                yAxisId="rainfall"
                xAxisId="time"
                dataKey="rainfall_level_mm"
                name={t('Rainfall Level (mm)')}
                stroke={rainfall}
                strokeWidth="3px"
                dot={false}
                activeDot={!props.show_water_level}
                data={rain_data}
              />
              <YAxis
                yAxisId="rainfall"
                dataKey="rainfall_level_mm"
                stroke={rainfall}
                orientation={props.show_water_level ? 'right' : 'left'}
                width={isMobile ? undefined : 70}
                domain={[0, 'dataMax + 1']}
                scale="linear"
                tickFormatter={value => formatTick(value, distance)}
              ></YAxis>
            </>
          )}
          {props.show_bench_height && (
            <ReferenceLine
              y={distance.from_metric(props.bench_height_mm ?? 0)}
              stroke={bench_height}
              strokeDasharray="3 3"
              xAxisId="time"
              yAxisId="water"
              label={{
                value: `${t('Bench Height:')} ${distance.show(props.bench_height_mm ?? 0)}`,
                position: 'bottom',
                fill: bench_height,
              }}
            />
          )}
          {props.show_pipe_diameter && (
            <ReferenceLine
              y={distance.from_metric(props.pipe_diameter_mm ?? 0)}
              stroke={pipe_diameter}
              strokeDasharray="3 3"
              xAxisId="time"
              yAxisId="water"
              label={{
                value: `${t('Pipe Diameter:')} ${pipeDiameterText}`,
                position: 'bottom',
                fill: pipe_diameter,
              }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
